import React, { useState } from 'react';
import { usePriceComponents } from '../hooks/usePriceComponents';
import { PointBadge } from '../helpers/PointBadge';
import { useSelector } from 'react-redux';
import { selectAllPoints } from '../map/slice/mapSelectors';
import { useTheme } from '../helpers/hooks';
import { titleize } from '../utils/stringUtils';
import { BooleanStatus } from '../helpers/BooleanStatus';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { CloseIcon, EditIcon, PlusIcon } from '@iconicicons/react';
import { PointSelectorModal } from '../points/PointSelectorModal';
import { Point, PriceComponent } from '@/types';
import toast from 'react-hot-toast';
import {
  useAttachPriceComponentPointMutation,
  useDetachPriceComponentPointMutation,
} from '../services/edgeApi';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { EditPriceComponentModal } from './EditPriceComponentModal';

export type PriceComponentTableProps = {};

export const PriceComponentTable: React.FC<PriceComponentTableProps> = ({}) => {
  const { priceComponents, isLoading, isFetching, exhausted, refresh, next } =
    usePriceComponents();
  const [attachPointTrigger] = useAttachPriceComponentPointMutation();
  const [detachPointTrigger] = useDetachPriceComponentPointMutation();
  const points = useSelector(selectAllPoints);
  const [theme] = useTheme();
  const [selectedPriceComponent, setSelectedPriceComponent] =
    useState<PriceComponent | null>(null);
  const [editPriceComponent, setEditPriceComponent] =
    useState<PriceComponent | null>(null);

  const detachPoint = async (component: PriceComponent, point: Point) => {
    if (
      !confirm(
        `Are you sure you want to detach ${component.description} from ${point?.name}?`,
      )
    )
      return;
    const result = await detachPointTrigger({
      component: component,
      point: point,
    });
    // @ts-ignore
    if (result.error) {
      toast.error(
        // @ts-ignore
        (result.error as any)?.data?.message || 'Failed to detach point',
      );
    } else {
      toast.success('Point detached successfully');
      refresh();
    }
  };

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Component</th>
            <th>TSP</th>
            <th>Status</th>
            <th>Buy/Sell</th>
            <th>Points</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(priceComponents).map((component) => {
            const tsp =
              points[component.points[0] ?? '']?.tsp_name || 'Unknown';
            const assigned = component.points.length > 0;
            const mapped = component.points.some((pointId) => {
              const point = points[pointId];
              return point?.node !== null && point?.node !== undefined;
            });
            return (
              <tr key={component.id}>
                <td>
                  <EditIcon
                    className="me-3"
                    role="button"
                    onClick={() => setEditPriceComponent(component)}
                  />
                  <b>#{component.id}</b> {component.description}
                </td>
                <td>{tsp}</td>
                <td>
                  {assigned ? (
                    <BooleanStatus
                      trueText="Mapped"
                      falseText="Not Mapped"
                      value={mapped}
                    />
                  ) : (
                    <BooleanStatus falseText="Not Assigned" value={false} />
                  )}
                </td>
                <td>{titleize(component.buy_sell)}</td>
                <td>
                  {component.points.map((pointId) => {
                    const point = points[pointId];
                    return (
                      <PointBadge
                        key={pointId}
                        point={point}
                        theme={theme}
                        extra_text={
                          <CloseIcon
                            role="button"
                            onClick={(evt) => {
                              detachPoint(component, point);
                              evt.stopPropagation();
                            }}
                          />
                        }
                      />
                    );
                  })}
                  <BadgeDisplay
                    role="button"
                    theme={theme}
                    onClick={() => setSelectedPriceComponent(component)}
                  >
                    <PlusIcon />
                  </BadgeDisplay>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!(isLoading || isFetching || exhausted) && (
        <InfiniteScrollObserver onScrollIn={next} />
      )}
      <PointSelectorModal
        isOpen={selectedPriceComponent !== null}
        onClose={() => setSelectedPriceComponent(null)}
        size="lg"
        onSelect={async (point) => {
          if (!selectedPriceComponent) return;
          try {
            await attachPointTrigger({
              point: point,
              component: selectedPriceComponent,
            }).unwrap();
            setSelectedPriceComponent(null);
            toast.success('Point attached successfully');
          } catch (error) {
            console.error('Failed to attach point:', error);
            toast.error(
              (error as any)?.data?.message ?? 'Failed to attach point',
            );
          }
          refresh();
        }}
      />
      <EditPriceComponentModal
        component={editPriceComponent}
        onClose={() => setEditPriceComponent(null)}
        afterSave={refresh}
      />
    </>
  );
};
